import { useState, useEffect, useCallback } from 'react'
import {
  Search,
  Users,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight,
  Coins,
  History,
  Gift,
  X,
  Eye,
  Lock,
  Unlock,
  AlertCircle,
  Building2,
  Shield,
  Loader2,
} from 'lucide-react'
import { medicalApi, usersApi, pointsApi, exchangesApi } from '../../lib/api'

interface PersonItem {
  id: string
  account_id: string
  name: string
  phone: string
  member_no?: string
  points: number
  status: string
  type: 'user' | 'medical'
  medical_role?: string
  hospital_id: string
  created_at: string
}

const MedicalUsers = () => {
  const currentUser = JSON.parse(localStorage.getItem('medicalUser') || '{}')

  const [items, setItems] = useState<PersonItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // 弹窗状态
  const [showModal, setShowModal] = useState(false)
  const [selectedPerson, setSelectedPerson] = useState<PersonItem | null>(null)
  const [modalType, setModalType] = useState<'view' | 'points' | 'freeze'>('view')

  const [pointChange, setPointChange] = useState('')
  const [pointReason, setPointReason] = useState('')
  const [pointLoading, setPointLoading] = useState(false)

  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyRecords, setHistoryRecords] = useState<any[]>([])
  const [historyPage, setHistoryPage] = useState(1)
  const [historyHasMore, setHistoryHasMore] = useState(false)
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false)

  const [showExchangeModal, setShowExchangeModal] = useState(false)
  const [exchangeLoading, setExchangeLoading] = useState(false)
  const [exchangeRecords, setExchangeRecords] = useState<any[]>([])

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params: Record<string, unknown> = {
        page: currentPage,
        page_size: pageSize,
      }
      if (searchTerm) params.keyword = searchTerm
      if (statusFilter !== 'all') params.status = statusFilter

      const res = await medicalApi.getUsersInHospital(params as any) as any
      setItems(res.items || [])
      setTotal(res.total || 0)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [currentPage, searchTerm, statusFilter])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  // 防抖搜索
  useEffect(() => {
    const t = setTimeout(() => setCurrentPage(1), 400)
    return () => clearTimeout(t)
  }, [searchTerm, statusFilter])

  // 积分历史
  useEffect(() => {
    if (showHistoryModal && selectedPerson?.id) {
      const isDifferent = historyRecords.length > 0 && historyRecords[0]?.user_id !== selectedPerson.id
      if (historyRecords.length === 0 || isDifferent) {
        setHistoryRecords([])
        handleViewHistory(selectedPerson.id, 1)
      }
    }
  }, [showHistoryModal, selectedPerson?.id])

  const totalPages = Math.ceil(total / pageSize)

  const maskPhone = (p: string) => p ? p.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : '-'

  const handleOpenModal = (person: PersonItem, type: 'view' | 'points' | 'freeze') => {
    setSelectedPerson(person)
    setModalType(type)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedPerson(null)
    setPointChange('')
    setPointReason('')
  }

  const handleFreeze = async () => {
    if (!selectedPerson) return
    try {
      if (selectedPerson.type === 'medical') {
        // 医护账号冻结/解冻
        const action = selectedPerson.status === 'frozen' ? 'unfreeze' : 'freeze'
        await medicalApi.updateStatus(selectedPerson.id, action)
      } else {
        // 普通用户
        if (selectedPerson.status === 'frozen') {
          await usersApi.unfreeze(selectedPerson.account_id)
        } else {
          await usersApi.freeze(selectedPerson.account_id)
        }
      }
      handleCloseModal()
      fetchUsers()
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const handleAdjustPoints = async () => {
    if (!selectedPerson || !pointChange || !pointReason) return
    setPointLoading(true)
    try {
      const delta = parseInt(pointChange)
      if (isNaN(delta)) { alert('请输入有效的积分数值'); return }
      await usersApi.adjustPoints(selectedPerson.id, delta, pointReason)
      handleCloseModal()
      fetchUsers()
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setPointLoading(false)
    }
  }

  const handleViewHistory = async (userId: string, page: number = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setHistoryLoadingMore(true)
    } else {
      setHistoryLoading(true)
    }
    try {
      const res = await pointsApi.records({ user_id: userId, page, page_size: 20 })
      const data = res as any
      const list = data.records || data.items || []
      if (isLoadMore) {
        setHistoryRecords(prev => [...prev, ...list])
      } else {
        setHistoryRecords(list)
      }
      setHistoryPage(page)
      const totalPages = data.total_pages || 1
      setHistoryHasMore(page < totalPages && list.length >= 20)
    } catch (e) {
      if (!isLoadMore) setHistoryRecords([])
    } finally {
      setHistoryLoading(false)
      setHistoryLoadingMore(false)
    }
  }

  const handleViewExchanges = async (userId: string) => {
    setExchangeLoading(true)
    try {
      const res = await exchangesApi.list({ user_id: userId, page_size: 20 })
      const data = res as any
      setExchangeRecords(data.items || data.exchanges || [])
    } catch (e) {
      setExchangeRecords([])
    } finally {
      setExchangeLoading(false)
    }
  }

  const getTypeBadge = (item: PersonItem) => {
    if (item.type === 'medical') {
      const color = item.medical_role === 'supervisor'
        ? 'bg-purple-100 text-purple-700'
        : 'bg-blue-100 text-blue-700'
      const label = item.medical_role === 'supervisor' ? '机构负责人' : '医护人员'
      return <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>{label}</span>
    }
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">普通用户</span>
  }

  const getStatusBadge = (status: string) => {
    if (status === 'frozen') return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">已冻结</span>
    if (status === 'inactive') return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">已注销</span>
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">正常</span>
  }

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">会员管理</h1>

      {/* 搜索过滤 */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="搜索姓名、手机号..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg">
          <option value="all">全部状态</option>
          <option value="active">正常</option>
          <option value="frozen">已冻结</option>
        </select>
      </div>

      {/* 统计 */}
      <div className="flex gap-4 text-sm text-gray-500">
        <span>共 <strong>{total}</strong> 位</span>
        <span>本页 {items.length} 条</span>
      </div>

      {/* 表格 */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">类型</th>
              <th className="px-4 py-3 text-left">姓名</th>
              <th className="px-4 py-3 text-left">手机号</th>
              <th className="px-4 py-3 text-right">积分</th>
              <th className="px-4 py-3 text-center">状态</th>
              <th className="px-4 py-3 text-left">注册时间</th>
              <th className="px-4 py-3 text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400">加载中...</td></tr>
            ) : error ? (
              <tr><td colSpan={7} className="text-center py-12 text-red-500">{error}</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400">暂无数据</td></tr>
            ) : items.map(item => (
              <tr key={item.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3">{getTypeBadge(item)}</td>
                <td className="px-4 py-3 font-medium">{item.name || '-'}</td>
                <td className="px-4 py-3">{maskPhone(item.phone || '')}</td>
                <td className="px-4 py-3 text-right">
                  {item.type === 'user' ? (
                    <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                      <Coins size={14} /> {item.points ?? 0}
                    </span>
                  ) : '-'}
                </td>
                <td className="px-4 py-3 text-center">{getStatusBadge(item.status)}</td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {item.created_at ? item.created_at.replace('T', ' ').slice(0, 19) : '-'}
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => handleOpenModal(item, 'view')}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="查看">
                      <Eye size={16} />
                    </button>
                    {item.type === 'user' && (
                      <button onClick={() => { setSelectedPerson(item); setShowHistoryModal(true) }}
                        className="p-1.5 text-purple-600 hover:bg-purple-50 rounded" title="积分历史">
                        <History size={16} />
                      </button>
                    )}
                    {item.type === 'user' && (
                      <button onClick={() => { setSelectedPerson(item); setShowExchangeModal(true); handleViewExchanges(item.id) }}
                        className="p-1.5 text-pink-600 hover:bg-pink-50 rounded" title="兑换记录">
                        <Gift size={16} />
                      </button>
                    )}
                    {item.type === 'user' && (
                      <button onClick={() => handleOpenModal(item, 'points')}
                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="调整积分">
                        <Coins size={16} />
                      </button>
                    )}
                    <button onClick={() => handleOpenModal(item, 'freeze')}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                      title={item.status === 'frozen' ? '解冻' : '冻结'}>
                      {item.status === 'frozen' ? <Unlock size={16} /> : <Lock size={16} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">第 {currentPage} / {totalPages} 页</span>
          <div className="flex gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* 查看/冻结 弹窗 */}
      {showModal && selectedPerson && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={handleCloseModal}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            {modalType === 'view' && (
              <>
                <h2 className="text-lg font-bold mb-4">详情</h2>
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between"><dt className="text-gray-500">类型</dt><dd>{selectedPerson.type === 'medical' ? (selectedPerson.medical_role === 'supervisor' ? '机构负责人' : '医护人员') : '普通用户'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">姓名</dt><dd>{selectedPerson.name}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">手机号</dt><dd>{selectedPerson.phone}</dd></div>
                  {selectedPerson.type === 'user' && (
                    <div className="flex justify-between"><dt className="text-gray-500">当前积分</dt><dd className="text-amber-600 font-medium">{selectedPerson.points ?? 0}</dd></div>
                  )}
                  <div className="flex justify-between"><dt className="text-gray-500">状态</dt><dd>{getStatusBadge(selectedPerson.status)}</dd></div>
                  {selectedPerson.member_no && (
                    <div className="flex justify-between"><dt className="text-gray-500">会员编号</dt><dd>{selectedPerson.member_no}</dd></div>
                  )}
                  <div className="flex justify-between"><dt className="text-gray-500">注册时间</dt><dd>{selectedPerson.created_at ? selectedPerson.created_at.replace('T', ' ').slice(0, 19) : '-'}</dd></div>
                </dl>
              </>
            )}

            {modalType === 'points' && (
              <>
                <h2 className="text-lg font-bold mb-4">调整积分 - {selectedPerson.name}</h2>
                <p className="text-sm text-gray-500 mb-3">当前积分: <strong className="text-amber-600">{selectedPerson.points ?? 0}</strong></p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">积分变动</label>
                    <input type="number" value={pointChange} onChange={e => setPointChange(e.target.value)}
                      placeholder="正增负减（如 100 或 -50）"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">调整原因</label>
                    <textarea value={pointReason} onChange={e => setPointReason(e.target.value)}
                      placeholder="必填" rows={3}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <button onClick={handleAdjustPoints} disabled={pointLoading}
                    className="w-full py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                    {pointLoading ? '提交中...' : '确认调整'}
                  </button>
                </div>
              </>
            )}

            {modalType === 'freeze' && (
              <>
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <AlertCircle className="text-red-500" size={20} />
                  {selectedPerson.status === 'frozen' ? '解冻账号' : '冻结账号'}
                </h2>
                <p className="text-gray-600 mb-4">
                  确定要{selectedPerson.status === 'frozen' ? '解冻' : '冻结'}<strong>{selectedPerson.name}</strong> 吗？
                  {selectedPerson.status !== 'frozen' && ' 冻结后该账号将无法登录。'}
                </p>
                <div className="flex gap-3">
                  <button onClick={handleCloseModal} className="flex-1 py-2 border rounded-lg hover:bg-gray-50">取消</button>
                  <button onClick={handleFreeze}
                    className={`flex-1 py-2 text-white rounded-lg ${selectedPerson.status === 'frozen' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                    确认{selectedPerson.status === 'frozen' ? '解冻' : '冻结'}
                  </button>
                </div>
              </>
            )}

            <button onClick={handleCloseModal}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* 积分历史弹窗 */}
      {showHistoryModal && selectedPerson && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowHistoryModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{selectedPerson.name} - 积分历史</h2>
              <button onClick={() => setShowHistoryModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            {historyLoading ? (
              <div className="flex-1 flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-green-600 border-t-transparent rounded-full" />
              </div>
            ) : historyRecords.length === 0 ? (
              <div className="text-center py-8 text-gray-400">暂无积分记录</div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-600">时间</th>
                      <th className="px-3 py-2 text-left text-gray-600">事件</th>
                      <th className="px-3 py-2 text-right text-gray-600">积分</th>
                      <th className="px-3 py-2 text-left text-gray-600">状态</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {historyRecords.map(r => (
                      <tr key={r.id}>
                        <td className="px-3 py-2 text-gray-500 text-xs">{r.created_at?.slice(0, 16)}</td>
                        <td className="px-3 py-2">{r.event_type_name || '-'}</td>
                        <td className={`px-3 py-2 text-right font-medium ${r.points > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {r.points > 0 ? '+' : ''}{r.points}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 text-xs rounded ${r.status === 'confirmed' ? 'bg-green-100 text-green-700' : r.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`}>
                            {r.status === 'confirmed' ? '已确认' : r.status === 'cancelled' ? '已撤销' : '待确认'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {historyHasMore && !historyLoadingMore && (
                  <button onClick={() => handleViewHistory(selectedPerson!.id, historyPage + 1, true)}
                    className="w-full py-2 text-center text-green-600 text-sm hover:bg-gray-50">
                    加载更多
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 兑换记录弹窗 */}
      {showExchangeModal && selectedPerson && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowExchangeModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{selectedPerson.name} - 兑换记录</h2>
              <button onClick={() => setShowExchangeModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            {exchangeLoading ? (
              <div className="flex-1 flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-green-600 border-t-transparent rounded-full" />
              </div>
            ) : exchangeRecords.length === 0 ? (
              <div className="text-center py-8 text-gray-400">暂无兑换记录</div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-600">时间</th>
                      <th className="px-3 py-2 text-left text-gray-600">礼品</th>
                      <th className="px-3 py-2 text-right text-gray-600">消耗积分</th>
                      <th className="px-3 py-2 text-left text-gray-600">状态</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {exchangeRecords.map(r => (
                      <tr key={r.id}>
                        <td className="px-3 py-2 text-gray-500 text-xs">{r.created_at?.slice(0, 16)}</td>
                        <td className="px-3 py-2">{r.gift_name || '-'}</td>
                        <td className="px-3 py-2 text-right font-medium text-red-500">-{r.points}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 text-xs rounded ${r.status === 'confirmed' ? 'bg-green-100 text-green-700' : r.status === 'pending' ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-500'}`}>
                            {r.status === 'confirmed' ? '已完成' : r.status === 'pending' ? '处理中' : '已取消'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MedicalUsers
